import { useEffect, useMemo, useState } from "react";
import Loading from "../shared-components/Loading";
import Table, { ReportEntry, ReportEntryStyle, ReportRowItem } from "./Table";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import PageHeader from "../shared-components/PageHeader";
import { useGetReportsSummarByStepNameList } from "../api/ReportsApi";
import { WorkflowByStepSummaryItem } from "../api/Workflows";
import {
  getMinDueDateFilter,
  getMaxDueDateFilter,
  dateWithAsterisk,
  formattedDateLong,
  validateWorkflowStatusDate,
  MAX_WORKFLOW_STATUS_DAYS_PAST,
} from "../utils";

import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import Pagination from "../shared-components/Pagination";

const WorkflowStatus = () => {
  const { workflowStepStatus } = useParams();
  const navigate = useNavigate();
  const pageSize = 10;

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [page, setPage] = useState(1);
  const [dateAdjustedWarning, setDateAdjustedWarning] = useState(false);

  // Get and validate the daysDue parameter
  const [daysDue] = useState(() => {
    const rawDaysDue = searchParams.get("daysDue") ?? "0";
    const { adjustedDate, wasAdjusted } =
      validateWorkflowStatusDate(rawDaysDue);
    if (wasAdjusted) {
      setDateAdjustedWarning(true);
      // Update only the daysDue parameter while preserving the hash-based routing path
      const hashParts = window.location.hash.split("?");
      const basePath = hashParts[0]; // e.g., "#/workflows/status/Web Content Review"
      const newParams = new URLSearchParams(searchParams);
      newParams.set("daysDue", adjustedDate);
      const newUrl = `${basePath}?${newParams.toString()}`;
      window.history.replaceState(null, "", newUrl);
    }
    return adjustedDate;
  });

  const minDueDate = useMemo(() => {
    const selectedDate = new Date(daysDue);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setUTCHours(0, 0, 0, 0);

    if (selectedDateStart < today) {
      // For past due dates, minDueDate is today - 10 days
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
      tenDaysAgo.setUTCHours(0, 0, 0, 0);
      return tenDaysAgo.toISOString();
    }

    // For today or future dates, minDueDate is the beginning of the selected day
    return getMinDueDateFilter(daysDue);
  }, [daysDue]);

  const maxDueDate = useMemo(() => {
    const selectedDate = new Date(daysDue);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setUTCHours(0, 0, 0, 0);

    if (selectedDateStart < today) {
      // For past due dates, maxDueDate is now
      return new Date().toISOString();
    }

    // For today or future dates, maxDueDate is the end of the selected day
    return getMaxDueDateFilter(daysDue);
  }, [daysDue]);

  const { data: reportsSummarByStepNameList, isLoading: loadingReports } =
    useGetReportsSummarByStepNameList(
      workflowStepStatus,
      page,
      pageSize,
      minDueDate,
      maxDueDate
    );

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(Number(searchParams.get("page")));
    }
  }, [searchParams]);

  const total = (): number => {
    return reportsSummarByStepNameList?.pagination?.total_count ?? 0;
  };

  if (loadingReports) {
    return <Loading />;
  }

  const convertReportsInProcessToRows = (
    reportsData: WorkflowByStepSummaryItem[],
    param: string
  ): ReportRowItem[] => {
    const reportRows: ReportRowItem[] = reportsData.map(
      (report: WorkflowByStepSummaryItem) => {
        const data: ReportEntry[] = [
          {
            text: report.full_name,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: report.report_uuid,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: report.company_name,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: report.blueprint_name,
            style: ReportEntryStyle.BADGE,
          },

          {
            text: "",
            style: ReportEntryStyle.COMPONENT,
            component: (
              <>
                {dateWithAsterisk(
                  new Date(report.due_date),
                  report?.due_date_updated ?? false,
                  "longUTC"
                )}
                <span
                  data-tip={`Local Time: ${formattedDateLong(report.due_date)}`}
                  className="cursor-pointer"
                >
                  <InformationCircleIcon className="h-4 w-4 text-gray-500 inline-block" />
                </span>
                <ReactTooltip
                  html={true}
                  className="hover:visible:opacity-1 w-80"
                  delayHide={10}
                  effect="solid"
                />
              </>
            ),
            customClass: "flex-1 whitespace-pre",
          },
        ];
        return {
          report: report,
          report_uuid: report.report_uuid,
          isLocked: report?.is_locked ?? false,
          data: data,
        };
      }
    );
    return reportRows;
  };

  const tableHeader = [
    "CANDIDATE NAME",
    "REPORT ID",
    "COMPANY",
    "CHECK TYPE",
    "DUE DATE",
  ];

  return (
    <>
      <Button
        text="Back"
        type={ButtonTypes.back}
        colorType={ButtonColorTypes.action}
        className="w-20 h-20 max-w-xs max-h-9 px-0 items-start my-1"
        onClick={() => navigate(-1)}
      />
      <PageHeader
        title={`Workflow Status : ${workflowStepStatus}`}
      ></PageHeader>
      <div>
        {dateAdjustedWarning && (
          <div className="bg-yellow-50 border border-yellow-400 rounded p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Date Range Adjusted
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    The requested date range has been adjusted to prevent query
                    timeouts. Workflow status queries are limited to{" "}
                    {MAX_WORKFLOW_STATUS_DAYS_PAST} days in the past.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <p className="text-sm pb-3">
          The table is organized by UTC timestamps.
        </p>
        {reportsSummarByStepNameList?.data && (
          <Table
            header={tableHeader}
            data={convertReportsInProcessToRows(
              reportsSummarByStepNameList.data,
              daysDue.toString()
            )}
          />
        )}
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={total()}
          search={""}
          params={{ daysDue: daysDue.toString() }}
        />
      </div>
    </>
  );
};

export default WorkflowStatus;
